import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft } from 'lucide-react';
interface StackedCouponCardsProps {
  onCouponClick: (coupon: any) => void;
  onSaveCoupon: (coupon: any) => void;
  savedCoupons: any[];
  category: string;
}
export function StackedCouponCards({
  onCouponClick,
  onSaveCoupon,
  savedCoupons,
  category
}: StackedCouponCardsProps) {
  // Sample coupons for different categories
  const allCoupons = {
    groceries: [{
      id: 101,
      title: 'RIMI',
      description: 'Weekly grocery discounts',
      highlight: '15%',
      color: '#1F2937',
      icon: '🛒',
      code: 'GROCERY15',
      expiryDate: '30.08.2023'
    }, {
      id: 102,
      title: 'MAXIMA',
      description: 'Fresh produce special',
      highlight: '5€',
      color: '#374151',
      icon: '🥦',
      code: 'FRESH5',
      expiryDate: '25.08.2023'
    }, {
      id: 103,
      title: 'SELVER',
      description: 'Weekend bulk buys',
      highlight: '20%',
      color: '#4B5563',
      icon: '🥫',
      code: 'BULK20',
      expiryDate: '27.08.2023'
    }],
    weekends: [{
      id: 201,
      title: 'BOLT',
      description: 'Weekend rides discount',
      highlight: '30%',
      color: '#1F2937',
      icon: '🚗',
      code: 'WEEKEND30',
      expiryDate: '31.08.2023'
    }, {
      id: 202,
      title: 'CINAMON',
      description: 'Movie night special',
      highlight: '2for1',
      color: '#374151',
      icon: '🍿',
      code: 'MOVIE2FOR1',
      expiryDate: '26.08.2023'
    }, {
      id: 203,
      title: 'HESBURGER',
      description: 'Weekend meal deal',
      highlight: '6€',
      color: '#4B5563',
      icon: '🍔',
      code: 'MEAL6',
      expiryDate: '27.08.2023'
    }],
    services: [{
      id: 301,
      title: 'WOLT',
      description: 'Free delivery on orders',
      highlight: 'FREE',
      color: '#1F2937',
      icon: '🛵',
      code: 'FREEDEL',
      expiryDate: '31.08.2023'
    }, {
      id: 302,
      title: 'SPOTIFY',
      description: 'Premium subscription',
      highlight: '50%',
      color: '#374151',
      icon: '🎵',
      code: 'SPOTIFY50',
      expiryDate: '25.08.2023'
    }, {
      id: 303,
      title: 'NETFLIX',
      description: 'First month discount',
      highlight: '40%',
      color: '#4B5563',
      icon: '📺',
      code: 'NETFLIX40',
      expiryDate: '30.08.2023'
    }],
    fashion: [{
      id: 401,
      title: 'SPORTLAND',
      description: 'Summer collection',
      highlight: '25%',
      color: '#1F2937',
      icon: '👟',
      code: 'SUMMER25',
      expiryDate: '31.08.2023'
    }, {
      id: 402,
      title: 'H&M',
      description: 'New arrivals',
      highlight: '15%',
      color: '#374151',
      icon: '👕',
      code: 'NEW15',
      expiryDate: '28.08.2023'
    }, {
      id: 403,
      title: 'ZARA',
      description: 'Seasonal sale',
      highlight: '30%',
      color: '#4B5563',
      icon: '👗',
      code: 'SEASON30',
      expiryDate: '29.08.2023'
    }],
    spa: [{
      id: 501,
      title: 'KALEV SPA',
      description: 'Relaxation package',
      highlight: '40%',
      color: '#1F2937',
      icon: '💆‍♀️',
      code: 'RELAX40',
      expiryDate: '31.08.2023',
      featured: true
    }, {
      id: 502,
      title: 'HARMOONIKUM',
      description: 'Wellness day pass',
      highlight: '2for1',
      color: '#374151',
      icon: '🧖‍♀️',
      code: 'WELLNESS2FOR1',
      expiryDate: '25.08.2023',
      featured: false
    }, {
      id: 503,
      title: 'BABOR SPA',
      description: 'Premium facial treatment',
      highlight: '35%',
      color: '#4B5563',
      icon: '✨',
      code: 'FACIAL35',
      expiryDate: '29.08.2023',
      featured: false
    }]
  };
  // If no coupons for the selected category, use groceries as default
  const coupons = allCoupons[category as keyof typeof allCoupons] || allCoupons.groceries;
  const isSpaCategory = category === 'spa';
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleCardClick = (index: number) => {
    if (expandedCard === index) {
      // If clicking the already expanded card, go to details
      onCouponClick(coupons[index]);
    } else if (expandedCard !== null) {
      // If another card is expanded, collapse it first
      setExpandedCard(null);
      setTimeout(() => setActiveIndex(index), 200);
    } else if (index === activeIndex) {
      // If clicking the top card, expand it
      setExpandedCard(index);
    } else {
      // Otherwise bring the card to the front
      setActiveIndex(index);
    }
  };
  const handleNext = () => {
    if (activeIndex < coupons.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };
  const handlePrev = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };
  // Reset expanded card when category changes
  useEffect(() => {
    setExpandedCard(null);
    setActiveIndex(0);
  }, [category]);
  return <div className="relative py-6" data-id="element-391">
      {isSpaCategory && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10" data-id="element-392">
          <motion.div className="bg-gray-900 text-white px-4 py-1 rounded-full flex items-center" initial={{
        y: -10,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-393">
            <span className="font-medium text-sm" data-id="element-394">Spa & Beauty Deals</span>
          </motion.div>
        </div>}
      {/* Card stack container */}
      <div ref={containerRef} className="relative h-[350px] md:h-[400px] w-full max-w-md mx-auto" data-id="element-395">
        <AnimatePresence initial={false} mode="popLayout" data-id="element-396">
          {coupons.map((coupon, index) => {
          const isExpanded = expandedCard === index;
          const isSaved = savedCoupons.some(saved => saved.id === coupon.id);
          // Calculate stacking positions
          const position = index - activeIndex;
          const isActive = index === activeIndex;
          return <motion.div key={coupon.id} className={`absolute inset-x-0 rounded-xl shadow-sm overflow-hidden cursor-pointer
                           ${isExpanded ? 'z-30 h-full' : 'h-[170px] md:h-[190px]'}
                           ${isSpaCategory && coupon.featured ? 'ring-1 ring-gray-200' : ''}
                           `} style={{
            backgroundColor: coupon.color,
            zIndex: coupons.length - Math.abs(position),
            width: isExpanded ? '100%' : undefined
          }} animate={{
            top: isExpanded ? 0 : `${Math.max(0, position * 50)}px`,
            left: isExpanded ? 0 : `${position * 8}px`,
            right: isExpanded ? 0 : `${-position * 8}px`,
            scale: isExpanded ? 1 : 1 - Math.abs(position) * 0.05,
            opacity: isExpanded || position >= -2 && position <= 2 ? 1 : 0
          }} transition={{
            type: 'tween',
            duration: 0.2
          }} onClick={() => handleCardClick(index)} data-id="element-397">
                {/* Featured tag for spa */}
                {isSpaCategory && coupon.featured && !isExpanded && <div className="absolute top-0 right-0 bg-white text-xs font-medium px-2 py-0.5" data-id="element-398">
                    FEATURED
                  </div>}
                <div className="p-5 text-white h-full flex flex-col" data-id="element-399">
                  {/* Card content */}
                  <div className="flex justify-between items-start" data-id="element-400">
                    <h3 className="text-xl font-bold" data-id="element-401">{coupon.title}</h3>
                    <span className="text-2xl" data-id="element-402">{coupon.icon}</span>
                  </div>
                  <div className="mt-2" data-id="element-403">
                    <p className="text-white/90 text-sm" data-id="element-404">
                      {coupon.description}
                    </p>
                    <div className="mt-3 text-4xl font-bold" data-id="element-405">
                      {coupon.highlight}
                    </div>
                  </div>
                  {/* Expanded content */}
                  {isExpanded && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="mt-4" data-id="element-406">
                      <div className="bg-white/20 p-4 rounded-lg" data-id="element-407">
                        <p className="font-mono text-center font-bold tracking-wider" data-id="element-408">
                          {coupon.code}
                        </p>
                        <p className="text-sm text-center mt-2" data-id="element-409">
                          Valid until {coupon.expiryDate}
                        </p>
                      </div>
                      <div className="mt-6 flex justify-between gap-3" data-id="element-410">
                        <motion.button className="flex-1 bg-white text-black py-2.5 rounded-lg font-medium" onClick={e => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(coupon.code);
                    onCouponClick(coupon);
                  }} whileTap={{
                    scale: 0.97
                  }} data-id="element-411">
                          Copy & Use
                        </motion.button>
                        <motion.button className={`px-4 rounded-lg ${isSaved ? 'bg-white text-black' : 'bg-white/30 text-white'}`} onClick={e => {
                    e.stopPropagation();
                    onSaveCoupon(coupon);
                  }} whileTap={{
                    scale: 0.97
                  }} data-id="element-412">
                          {isSaved ? 'Saved' : 'Save'}
                        </motion.button>
                      </div>
                    </motion.div>}
                </div>
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
      {/* Navigation controls */}
      {expandedCard === null && <div className="flex justify-between mt-4" data-id="element-413">
          <button onClick={handlePrev} disabled={activeIndex === 0} className={`p-2 rounded-full ${activeIndex === 0 ? 'text-gray-300 cursor-default' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-414">
            <ChevronLeft size={20} data-id="element-415" />
          </button>
          <div className="flex gap-1 items-center" data-id="element-416">
            {coupons.map((_, index) => <button key={index} onClick={() => setActiveIndex(index)} className={`w-2 h-2 rounded-full transition-all ${index === activeIndex ? 'bg-gray-900' : 'bg-gray-300'}`} data-id="element-417" />)}
          </div>
          <button onClick={handleNext} disabled={activeIndex === coupons.length - 1} className={`p-2 rounded-full ${activeIndex === coupons.length - 1 ? 'text-gray-300 cursor-default' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-418">
            <ChevronRight size={20} data-id="element-419" />
          </button>
        </div>}
    </div>;
}